import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { User, Phone, Users, LogOut, ChevronRight, Calendar, Plus, Trash2, ShieldAlert, X, Heart } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader } from "../components/ui-kit";
import { CountryPicker, CityPicker } from "../components/LocationPickers";

const Sheet = ({ open, onClose, title, children }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center" data-testid="sheet-overlay">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-[430px] bg-white rounded-t-[32px] p-5 pb-8 max-h-[85dvh] overflow-y-auto">
        <div className="w-12 h-1.5 rounded-full bg-[#E7E5E4] mx-auto mb-4" />
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-instrument text-xl font-semibold text-[#0A4C36]">{title}</h3>
          <button onClick={onClose} data-testid="sheet-close"><X size={20} className="text-[#78716C]" /></button>
        </div>
        {children}
      </div>
    </div>
  );
};

const Profile = () => {
  const { user, logout, setUser } = useAuth();
  const nav = useNavigate();
  const [family, setFamily] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [emergencyOpen, setEmergencyOpen] = useState(false);
  const [familyOpen, setFamilyOpen] = useState(false);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/patient/family").then((r) => setFamily(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => { setForm(user || {}); }, [user]);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const saveProfile = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/patient/profile", form);
      setUser(data);
      localStorage.setItem("hc_user", JSON.stringify(data));
      setEditOpen(false);
      setEmergencyOpen(false);
    } finally { setBusy(false); }
  };

  const addMember = async (payload) => {
    const { data } = await api.post("/patient/family", payload);
    setFamily([...family, data]);
  };
  const delMember = async (id) => {
    await api.delete(`/patient/family/${id}`);
    setFamily(family.filter((f) => f.id !== id));
  };

  return (
    <div className="pb-28">
      <PageHeader title="Profile" subtitle={user?.email} />

      {/* Identity card */}
      <div className="px-5">
        <div className="card">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-3xl bg-gradient-to-br from-[#F4844B] to-[#EF4444] text-white flex items-center justify-center font-instrument text-2xl font-semibold shadow-lg shadow-[#F4844B]/25">
              {(user?.name || "P").slice(0, 1).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-instrument text-xl font-semibold text-[#1C1917]">{user?.name}</div>
              <div className="text-sm text-[#78716C]">{user?.mobile || "No mobile"} · {user?.city || "—"}</div>
              {user?.blood_group && (
                <div className="inline-block mt-2 px-2.5 py-1 rounded-full bg-red-50 text-red-500 text-xs font-medium">
                  Blood: {user.blood_group}
                </div>
              )}
            </div>
          </div>
          <button data-testid="profile-edit" onClick={() => setEditOpen(true)} className="mt-4 btn-secondary text-sm py-2.5">
            Edit personal info
          </button>
        </div>
      </div>

      {/* Emergency contact */}
      <div className="px-5 mt-4">
        <div className="card">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-2xl bg-red-50 text-red-500 flex items-center justify-center">
              <ShieldAlert size={20} />
            </div>
            <div className="flex-1">
              <div className="font-instrument font-semibold text-[#1C1917]">Emergency contact</div>
              <div className="text-xs text-[#78716C]">Alerted along with SOS</div>
            </div>
          </div>
          {user?.emergency_contact_mobile ? (
            <div className="mt-2 flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-[#1C1917]">{user.emergency_contact_name}</div>
                <div className="text-xs text-[#78716C]">{user.emergency_contact_relation} · {user.emergency_contact_mobile}</div>
              </div>
              <button data-testid="profile-emergency-edit" onClick={() => setEmergencyOpen(true)} className="text-sm text-[#0A4C36] font-medium">Edit</button>
            </div>
          ) : (
            <button data-testid="profile-emergency-add" onClick={() => setEmergencyOpen(true)} className="btn-secondary text-sm py-2.5 mt-2">
              + Add emergency contact
            </button>
          )}
        </div>
      </div>

      {/* Health details */}
      <div className="px-5 mt-4">
        <div className="card">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-2xl bg-[#FCD9C8] text-[#F4844B] flex items-center justify-center">
              <Heart size={18} />
            </div>
            <div className="font-instrument font-semibold text-[#1C1917]">Health snapshot</div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <MiniStat label="Height" value={user?.height_cm ? `${user.height_cm} cm` : "—"} />
            <MiniStat label="Weight" value={user?.weight_kg ? `${user.weight_kg} kg` : "—"} />
            <MiniStat label="Blood group" value={user?.blood_group || "—"} />
            <MiniStat label="Allergies" value={user?.allergies || "None"} truncate />
          </div>
        </div>
      </div>

      {/* Menu list */}
      <div className="px-5 mt-4 space-y-2">
        <MenuItem to="/appointments" icon={Calendar} label="Appointment history" testid="menu-appointments" />
        <button data-testid="menu-family" onClick={() => setFamilyOpen(true)} className="w-full">
          <MenuRow icon={Users} label={`Family members (${family.length})`} />
        </button>
      </div>

      <div className="px-5 mt-6">
        <button data-testid="profile-logout" onClick={() => { logout(); nav("/login"); }}
          className="w-full py-4 rounded-2xl bg-white border border-red-100 text-red-500 font-medium flex items-center justify-center gap-2">
          <LogOut size={18} /> Log out
        </button>
      </div>

      {/* Edit profile sheet */}
      <Sheet open={editOpen} onClose={() => setEditOpen(false)} title="Personal info">
        <div className="space-y-3">
          <Field label="Name"><input data-testid="edit-name" className="input-field" value={form.name || ""} onChange={upd("name")} /></Field>
          <Field label="Mobile"><input data-testid="edit-mobile" className="input-field" value={form.mobile || ""} onChange={upd("mobile")} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Height (cm)"><input type="number" data-testid="edit-height" className="input-field" value={form.height_cm || ""} onChange={upd("height_cm")} /></Field>
            <Field label="Weight (kg)"><input type="number" data-testid="edit-weight" className="input-field" value={form.weight_kg || ""} onChange={upd("weight_kg")} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Blood group">
              <select data-testid="edit-blood" className="input-field" value={form.blood_group || ""} onChange={upd("blood_group")}>
                <option value="">Select</option>{["A+","A-","B+","B-","AB+","AB-","O+","O-"].map((g) => <option key={g}>{g}</option>)}
              </select>
            </Field>
            <Field label="Country">
              <CountryPicker
                testid="edit-country"
                value={form.country || ""}
                onChange={(code) => setForm({ ...form, country: code, city: "" })}
              />
            </Field>
          </div>
          <Field label="City">
            <CityPicker
              testid="edit-city"
              countryCode={form.country || ""}
              value={form.city || ""}
              onChange={(v) => setForm({ ...form, city: v })}
            />
          </Field>
          <Field label="Allergies"><input data-testid="edit-allergies" className="input-field" value={form.allergies || ""} onChange={upd("allergies")} /></Field>
          <Field label="Medical conditions"><input data-testid="edit-conditions" className="input-field" value={form.medical_conditions || ""} onChange={upd("medical_conditions")} /></Field>
          <button data-testid="edit-save" disabled={busy} onClick={saveProfile} className="btn-primary mt-2">{busy ? "Saving…" : "Save"}</button>
        </div>
      </Sheet>

      {/* Emergency contact sheet */}
      <Sheet open={emergencyOpen} onClose={() => setEmergencyOpen(false)} title="Emergency contact">
        <div className="space-y-3">
          <Field label="Name"><input data-testid="ec-name" className="input-field" value={form.emergency_contact_name || ""} onChange={upd("emergency_contact_name")} /></Field>
          <Field label="Relation"><input data-testid="ec-relation" className="input-field" value={form.emergency_contact_relation || ""} onChange={upd("emergency_contact_relation")} /></Field>
          <Field label="Mobile"><input data-testid="ec-mobile" className="input-field" value={form.emergency_contact_mobile || ""} onChange={upd("emergency_contact_mobile")} /></Field>
          <button data-testid="ec-save" disabled={busy} onClick={saveProfile} className="btn-primary mt-2">{busy ? "Saving…" : "Save contact"}</button>
        </div>
      </Sheet>

      {/* Family sheet */}
      <Sheet open={familyOpen} onClose={() => setFamilyOpen(false)} title="Family members">
        <FamilyManager list={family} onAdd={addMember} onDel={delMember} />
      </Sheet>
    </div>
  );
};

const MiniStat = ({ label, value, truncate }) => (
  <div className="bg-[#FFF7EE] rounded-2xl p-3">
    <div className="text-xs text-[#78716C]">{label}</div>
    <div className={`font-instrument text-base font-medium text-[#1C1917] mt-0.5 ${truncate ? "truncate" : ""}`}>{value}</div>
  </div>
);

const MenuItem = ({ to, icon: Icon, label, testid }) => (
  <Link to={to} data-testid={testid} className="block no-underline">
    <MenuRow icon={Icon} label={label} />
  </Link>
);

const MenuRow = ({ icon: Icon, label }) => (
  <div className="card flex items-center gap-3 !py-4">
    <div className="w-10 h-10 rounded-2xl bg-[#E6F0EC] text-[#0A4C36] flex items-center justify-center">
      <Icon size={18} />
    </div>
    <div className="flex-1 text-left font-medium text-[#1C1917]">{label}</div>
    <ChevronRight size={18} className="text-[#78716C]" />
  </div>
);

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-medium text-[#57534E] ml-1">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);

const FamilyManager = ({ list, onAdd, onDel }) => {
  const [f, setF] = useState({ name: "", relation: "", gender: "", dob: "", mobile: "" });
  const [busy, setBusy] = useState(false);
  const add = async () => {
    if (!f.name || !f.relation) return;
    setBusy(true);
    try {
      const payload = { ...f };
      Object.keys(payload).forEach((k) => { if (!payload[k]) delete payload[k]; });
      await onAdd(payload);
      setF({ name: "", relation: "", gender: "", dob: "", mobile: "" });
    } finally { setBusy(false); }
  };
  return (
    <div>
      <div className="space-y-2 mb-4">
        {list.length === 0 && <div className="text-sm text-[#78716C] text-center py-3">No family members yet.</div>}
        {list.map((m) => (
          <div key={m.id} data-testid={`family-item-${m.id}`} className="card !py-3 flex items-center gap-3">
            <User size={18} className="text-[#0A4C36]" />
            <div className="flex-1 min-w-0">
              <div className="font-medium text-[#1C1917] text-sm">{m.name}</div>
              <div className="text-xs text-[#78716C]">{m.relation}{m.mobile ? ` · ${m.mobile}` : ""}</div>
            </div>
            <button data-testid={`family-del-${m.id}`} onClick={() => onDel(m.id)} className="w-9 h-9 rounded-xl bg-red-50 text-red-500 flex items-center justify-center">
              <Trash2 size={16} />
            </button>
          </div>
        ))}
      </div>
      <div className="pt-3 border-t border-[#0A4C36]/5 space-y-2">
        <input data-testid="family-name" className="input-field" placeholder="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        <input data-testid="family-relation" className="input-field" placeholder="Relation (e.g. Father)" value={f.relation} onChange={(e) => setF({ ...f, relation: e.target.value })} />
        <div className="grid grid-cols-2 gap-2">
          <select data-testid="family-gender" className="input-field" value={f.gender} onChange={(e) => setF({ ...f, gender: e.target.value })}>
            <option value="">Gender</option><option>Male</option><option>Female</option><option>Other</option>
          </select>
          <input data-testid="family-dob" type="date" className="input-field" value={f.dob} onChange={(e) => setF({ ...f, dob: e.target.value })} />
        </div>
        <input data-testid="family-mobile" className="input-field" placeholder="Mobile (optional)" value={f.mobile} onChange={(e) => setF({ ...f, mobile: e.target.value })} />
        <button data-testid="family-add" disabled={busy} onClick={add} className="btn-primary text-sm py-3 flex items-center justify-center gap-2">
          <Plus size={16} /> {busy ? "Adding…" : "Add member"}
        </button>
      </div>
    </div>
  );
};

export default Profile;
